import { useMemo } from 'react';
import { components, type GroupBase, type ValueContainerProps } from 'react-select';
import Select, {
    PlaceholderSizer,
    selectedValueLabel,
    type RowOption,
    type SelectOption,
    type SelectProps,
} from '~/components/ui/forms/Select';
import type { IconNames } from '~/components/ui/Icon';
import Label from '~/components/ui/Label';
import '~/styles/components/new/dropdown.scss';
import { classNames } from '~/utils/styles';
import { ChipTag, type ChipTagProps } from './Chip';
import type { MenuRowCustomProps, MenuRowToggleProps } from './Menu';

export type ChipTagOption<T extends string | number = any, L = any> = SelectOption<T, L> &
    ChipTagProps;
export type TextOption = SelectOption;
export type MixedOption = SelectOption & (MenuRowCustomProps | MenuRowToggleProps);

export type DropDownProps = Omit<SelectProps<string | number>, 'options'> & {
    icon?: IconNames;
    size?: 'medium' | 'large';
    options: (RowOption | GroupBase<RowOption>)[];
    typology?: 'text' | 'tag';
    'data-test-component'?: string;
    'data-test-name'?: string;
};

export default function DropDown({
    isClearable = true,
    isSearchable = false,
    className = '',
    typology = 'text',
    icon,
    options,
    size = 'large',
    'data-test-component': testComponent = 'new/DropDown',
    'data-test-name': testName,
    ...selectProps
}: DropDownProps) {
    const customComponents = useMemo(
        () => ({
            ...(selectProps.components ?? {}),
            ValueContainer: ({ children, ...containerProps }: ValueContainerProps) => {
                const { selectProps, hasValue } = containerProps;
                return (
                    <Label icon={icon}>
                        <components.ValueContainer {...containerProps}>
                            <PlaceholderSizer
                                classNamePrefix={selectProps.classNamePrefix}
                                hasValue={hasValue}
                                inputValue={selectProps.inputValue}
                                isMulti={selectProps.isMulti}
                                isSearchable={selectProps.isSearchable}
                                placeholder={selectProps.placeholder}
                                valueLabel={selectedValueLabel(containerProps)}
                            />
                            {children}
                        </components.ValueContainer>
                    </Label>
                );
            },
        }),
        [icon, selectProps.components],
    );

    return (
        <Select
            {...selectProps}
            isSearchable={isSearchable}
            isClearable={isClearable}
            growable={selectProps.growable ?? true}
            className={classNames({
                [`dropdown size-${size} typology-${typology}`]: true,
                'no-icon': !icon,
                'has-value': Boolean(selectProps.value),
                [className]: Boolean(className),
            })}
            components={customComponents}
            getOptionLabel={(option: SelectOption | ChipTagOption) => {
                if (typology === 'tag') {
                    return <ChipTag {...((option as ChipTagOption) || {})}>{option.label}</ChipTag>;
                }
                return option.label;
            }}
            options={typology === 'tag' ? options.map((o) => ({ ...o, type: 'tag' })) : options}
            data-test-component={testComponent}
            data-test-name={testName}
        />
    );
}

export function QuickFilterDropDown({ className = '', ...props }: DropDownProps) {
    return (
        <DropDown
            className={classNames({
                'quick-filter': true,
                [className]: Boolean(className),
            })}
            {...props}
        />
    );
}
